import { useEffect, useMemo, useState } from 'react'
import { Outlet } from 'react-router'
import { useRole } from '@/auth/useRole'
import { isSearchShortcut } from '@/lib/platform'
import { CommandSearch } from './CommandSearch'
import { CommandSearchContext } from './searchContext'

/**
 * Layout route around every staff page, the landing page and /taken included
 * (spec-62): owns the ⌘K / Ctrl+K shortcut and the palette, and lets the search
 * buttons in WorkspaceLayout open it. Founders never get here (the founder routes
 * sit outside it); the fundraiser gets nothing because the endpoint is admin/team
 * only. UX gate only: the backend enforces RBAC.
 */
export function CommandSearchHost() {
  const { role } = useRole()
  const available = role === 'admin' || role === 'team'
  const [open, setOpen] = useState(false)

  useEffect(() => {
    if (!available) return
    function onKeyDown(event: KeyboardEvent) {
      if (event.repeat || !isSearchShortcut(event)) return
      event.preventDefault()
      setOpen((current) => !current)
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [available])

  const control = useMemo(() => ({ available, open: () => setOpen(true) }), [available])

  return (
    <CommandSearchContext.Provider value={control}>
      <Outlet />
      {available && open ? <CommandSearch onClose={() => setOpen(false)} /> : null}
    </CommandSearchContext.Provider>
  )
}
